/**
 * postMessage contract between the overlay payment host (`EmbeddedPaymentHost`,
 * running in the widget's shadow root on the partner origin) and the Dorsia-origin
 * payment iframe (`EmbeddedPaymentFrame`, at `book.dorsia.com/embed/pay`).
 *
 * Why an iframe at all: Stripe.js refuses to mount Elements inside a shadow root
 * (`IntegrationError: Elements cannot be mounted in a ShadowRoot`). The iframe is a
 * separate light-DOM document on Dorsia's own origin, so Stripe mounts normally and
 * the card DOM stays out of the host page's reach.
 *
 * Security model:
 *  - Every message carries a discriminating `source` and a per-session `nonce`
 *    (minted by the host, passed to the iframe via its URL). Both sides validate
 *    `event.origin` AND the nonce before trusting a message.
 *  - The bearer token never crosses into the iframe. The host mints the SetupIntent
 *    and passes only its `clientSecret`; the iframe confirms card setup and returns
 *    the resulting Stripe payment-method id, which the host attaches with its token.
 */

export const EMBED_PAY_SOURCE = 'dorsia-embed-pay' as const;

/** Child → Host: iframe has mounted and is ready to receive `init`. */
export interface ReadyMessage {
  source: typeof EMBED_PAY_SOURCE;
  type: 'ready';
  nonce: string;
}

/** Host → Child: hand the iframe the SetupIntent secret + billing defaults. */
export interface InitMessage {
  source: typeof EMBED_PAY_SOURCE;
  type: 'init';
  nonce: string;
  clientSecret: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

/** Child → Host: the rendered form's pixel height changed (responsive sizing). */
export interface ResizeMessage {
  source: typeof EMBED_PAY_SOURCE;
  type: 'resize';
  nonce: string;
  height: number;
}

/** Child → Host: card confirmed; hand back the tokenized method for attach. */
export interface SuccessMessage {
  source: typeof EMBED_PAY_SOURCE;
  type: 'success';
  nonce: string;
  paymentMethodId: string;
  brand: string;
  last4: string;
}

/**
 * Child → Host: something went wrong (Stripe error, load failure, etc.).
 *
 * `errorKind` tells the host how to react:
 *  - `'fatal'`      — non-recoverable (bad/missing URL params, `loadStripe`
 *                     failure, an unusable `confirmCardSetup` result). The host
 *                     shows a Retry that re-mints the SetupIntent and re-frames.
 *  - `'card'`       — a recoverable card problem. The iframe keeps these INLINE
 *                     in the card form, so this variant is reserved for the rare
 *                     case a card issue must escalate to the host as a banner.
 */
export interface ErrorMessage {
  source: typeof EMBED_PAY_SOURCE;
  type: 'error';
  nonce: string;
  errorKind: 'fatal' | 'card';
  message: string;
}

export type EmbedPayMessage =
  | ReadyMessage
  | InitMessage
  | ResizeMessage
  | SuccessMessage
  | ErrorMessage;

/** Narrow an arbitrary `MessageEvent.data` to one of our messages with a matching nonce. */
export function parseEmbedPayMessage(data: unknown, expectedNonce: string): EmbedPayMessage | null {
  if (typeof data !== 'object' || data === null) return null;
  const msg = data as Record<string, unknown>;
  if (msg.source !== EMBED_PAY_SOURCE) return null;
  if (typeof msg.nonce !== 'string' || msg.nonce !== expectedNonce) return null;
  if (typeof msg.type !== 'string') return null;
  return msg as unknown as EmbedPayMessage;
}
